import React, { useState, useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import { SectionHeader } from '../../components/SectionHeader.js';
import { Card } from '../../components/Card.js';
import { Button } from '../../components/Button.js';
import { formatCurrency } from '../../utils/formatters.js';

interface RoiCalculatorProps {
  onOpenLeadModal: (source: string) => void;
}

export const RoiCalculator: React.FC<RoiCalculatorProps> = ({ onOpenLeadModal }) => {
  // プラン定義（月額概算単価/円）
  const plans = [
    { name: 'Business Standard', price: 1632, desc: '月額 ¥1,632 / ユーザー' },
    { name: 'Business Plus', price: 2448, desc: '月額 ¥2,448 / ユーザー (Vault標準)' },
    { name: 'Enterprise Standard', price: 3264, desc: '月額 ¥3,264 / ユーザー' },
    { name: 'Enterprise Plus', price: 4080, desc: '月額 ¥4,080 / ユーザー' },
  ];

  const [selectedPlanIndex, setSelectedPlanIndex] = useState(1); // Business Plus default
  const [retireesPerYear, setRetireesPerYear] = useState(50); // 年間退職者数
  const [retentionYears, setRetentionYears] = useState(3); // 従来保持していた年数

  const annualSavings = useMemo(() => {
    const monthlyPrice = plans[selectedPlanIndex].price;
    // 年間退職者 × 月額単価 × 12ヶ月
    return retireesPerYear * monthlyPrice * 12;
  }, [selectedPlanIndex, retireesPerYear]);

  const cumulativeSavings = useMemo(() => {
    // 過去の退職者を放置していた場合の累積削減効果
    return annualSavings * retentionYears;
  }, [annualSavings, retentionYears]);

  return (
    <section id="roi" className="py-20 md:py-28 relative border-t border-slate-200/80 bg-slate-100/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          tag="ROI & COST OPTIMIZATION"
          tagVariant="emerald"
          title="退職者ライセンス削減シミュレーター"
          description="データ保全のために退職者の Google Workspace アカウントを削除できずにいませんか？ ARAYA のスマートオフボーディングにより、保全完了後にライセンスを即時回収した場合の削減効果を試算できます。"
        />

        <div className="max-w-4xl mx-auto">
          <Card glow="emerald" className="p-8 sm:p-12 bg-white border-slate-200 shadow-md">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              {/* Input Controls */}
              <div className="lg:col-span-7 space-y-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2.5">
                    ご利用中の Google Workspace エディション
                  </label>
                  <div className="grid grid-cols-2 gap-2.5">
                    {plans.map((p, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setSelectedPlanIndex(idx)}
                        className={`p-3.5 rounded-2xl text-left border transition-all text-xs ${
                          selectedPlanIndex === idx
                            ? 'bg-emerald-50/80 border-emerald-500 text-emerald-950 shadow-xs ring-1 ring-emerald-500/30'
                            : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <div className="font-bold text-slate-800">{p.name}</div>
                        <div className="text-[11px] text-emerald-700 font-medium mt-0.5">{p.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      年間退職者・アカウント整理数
                    </label>
                    <span className="text-base font-mono font-bold text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200">
                      {retireesPerYear} 名 / 年
                    </span>
                  </div>
                  <input
                    type="range"
                    min="5"
                    max="500"
                    step="5"
                    value={retireesPerYear}
                    onChange={(e) => setRetireesPerYear(Number(e.target.value))}
                    className="w-full h-2.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                  />
                  <div className="flex justify-between text-[11px] text-slate-400 font-mono mt-1">
                    <span>5名</span>
                    <span>100名</span>
                    <span>250名</span>
                    <span>500名</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      データ保全・保管想定期間
                    </label>
                    <span className="text-base font-mono font-bold text-sky-700 bg-sky-50 px-2.5 py-0.5 rounded-full border border-sky-200">
                      {retentionYears} 年間
                    </span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="7"
                    step="1"
                    value={retentionYears}
                    onChange={(e) => setRetentionYears(Number(e.target.value))}
                    className="w-full h-2.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-sky-600"
                  />
                  <div className="flex justify-between text-[11px] text-slate-400 font-mono mt-1">
                    <span>1年</span>
                    <span>3年 (一般的)</span>
                    <span>5年</span>
                    <span>7年 (法定監査)</span>
                  </div>
                </div>
              </div>

              {/* Result Preview Box */}
              <div className="lg:col-span-5 bg-gradient-to-br from-emerald-600 via-teal-700 to-emerald-800 rounded-3xl p-6 sm:p-8 flex flex-col justify-between text-center relative overflow-hidden text-white shadow-xl">
                <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none" />

                <div>
                  <span className="text-xs font-mono px-3 py-1 rounded-full bg-white/20 text-white font-semibold inline-block mb-5 backdrop-blur-sm">
                    ESTIMATED COST CUT
                  </span>
                  
                  <div className="mb-5">
                    <p className="text-xs text-emerald-100 mb-1">年間削減ライセンス費用</p>
                    <p className="text-3xl sm:text-4xl font-extrabold text-white font-mono tracking-tight">
                      {formatCurrency(annualSavings)}
                    </p>
                    <p className="text-[11px] text-emerald-200 font-mono mt-1">/ 年間削減見込額</p>
                  </div>

                  <div className="pt-5 border-t border-emerald-400/30 mb-6">
                    <p className="text-xs text-emerald-100 mb-1">{retentionYears}年間の累積コスト削減効果</p>
                    <p className="text-2xl font-bold text-sky-200 font-mono">
                      {formatCurrency(cumulativeSavings)}
                    </p>
                  </div>
                </div>

                <Button
                  variant="secondary"
                  size="md"
                  className="w-full text-xs font-bold bg-white text-emerald-900 hover:bg-emerald-50 border-transparent shadow-md"
                  icon={<ArrowRight className="w-4 h-4 text-emerald-700" />}
                  onClick={() => onOpenLeadModal('roi_calculator')}
                >
                  詳細なコスト削減試算表を取り寄せる
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};
